// src/components/LandingPage.jsx

import { useEffect, useState, useCallback } from "react";
import { placeholderImage, toPublicStorageUrl } from "../config/imageConfig";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { fetchFeaturedProducts } from "../api/homePageProductsapi";
import { getUserProfile } from "../utils/auth";
import image from "../images/image.jpg";
import imagesDashboard from "../images/ImagesDashBoard2.jpg";
import imagesDashboard3 from "../images/ImagesDashBoard3.png";
import FashionImage1 from "../images/fashion-stylish-model-dressed-elegant-light-pink-suit.jpg";
import FashionImage2 from "../images/Jewelry-laydown-two.jpg";
import FashionImage3 from "../images/left-1.jpg";
import { useNavigate } from "react-router-dom";
import WishlistToggle from "./WishlistToggle";

// Helper to safely get image URL — returns null if invalid
const getImageUrl = (rawUrl) => {
  if (!rawUrl) return null;
  const cleaned = toPublicStorageUrl(rawUrl.trim());
  return cleaned || null;
};

/** How long each hero slide holds; also drives the progress line's fill. */
const HERO_AUTOPLAY_MS = 10000;

// Hero images data - moved outside component to prevent recreation on every render
const heroImages = [
  {
    src: image,
    alt: "Hero Fashion",
    top_label: "Autumn–Winter 2026",
    Title: "Dressed for the season ahead",
    Buttom_label: "Tailoring, knitwear and outerwear, chosen with you in mind.",
    Total_Button: 2,
    ButtonsName: [{ name: "Shop Women" }, { name: "Shop Men" }],
  },
  {
    src: imagesDashboard,
    alt: "Dashboard",
    top_label: "The edit",
    Title: "Pieces for every occasion",
    Buttom_label: "From the first meeting to the last dinner reservation.",
    Total_Button: 2,
    ButtonsName: [{ name: "Shop collections" }, { name: "View lookbook" }],
  },
  {
    src: imagesDashboard3,
    alt: "Dashboard",
    top_label: "Limited edition",
    Title: "Made quietly, made to last",
    Buttom_label: "Small runs in considered fabrics, while they last.",
    Total_Button: 2,
    ButtonsName: [{ name: "Discover more" }, { name: "New arrivals" }],
  },
];

// Shop-by-category magazine grid. `size` places the tile (one tall portrait
// on the left, two wide tiles stacked on the right); `focus` is the crop
// anchor so each photo keeps its subject in the tile's shape; `event` is the
// window event Navbar listens for to route to the category page.
const Sectiontwoimages = [
  {
    key: "women",
    src: FashionImage3,
    width: 1064,
    height: 1304,
    eyebrow: "Women",
    title: "The autumn edit",
    link: "Shop women",
    size: "tall",
    focus: "50% 30%",
    event: "openWomenMenu",
  },
  {
    key: "men",
    src: FashionImage1,
    width: 1600,
    height: 1234,
    eyebrow: "Men",
    title: "Tailoring & knitwear",
    link: "Shop men",
    size: "wide",
    focus: "55% 18%",
    event: "openMenMenu",
  },
  {
    key: "accessories",
    src: FashionImage2,
    width: 1600,
    height: 1067,
    eyebrow: "Accessories",
    title: "Finishing touches",
    link: "Shop accessories",
    size: "wide",
    focus: "50% 50%",
    event: "openAccessoriesMenu",
  },
];

/** One product card, used by every rail and grid on the page.
 *
 *  Declared at module scope on purpose: a component defined inside a render
 *  body gets a new type on every render, so React remounts every card and
 *  replays its entrance animation — and this page re-renders once a second
 *  from the auth poll. `onOpen` replaces what used to be a closure. */
function ProductCard({ product, badge, ordinal, onOpen }) {
  const img = getImageUrl(product.image);
  const hasDiscount =
    product.discounted_price != null &&
    product.discounted_price > 0 &&
    product.discounted_price < product.price;
  const shown = hasDiscount ? product.discounted_price : product.price;

  return (
    <article className="ob-card">
      <div className="ob-shot" onClick={() => onOpen(product)}>
        {product.discount_percent > 0 ? (
          <span className="ob-tagline ob-tagline-sale">
            {product.discount_percent}% off
          </span>
        ) : badge ? (
          <span className="ob-tagline">{badge}</span>
        ) : null}

        {ordinal ? <span className="ob-ord">{ordinal}</span> : null}

        {img ? (
          <img
            src={img}
            alt={product.enhanced_name || "Product"}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.target.src = placeholderImage(product.id || "orbiee", 400, 534);
            }}
          />
        ) : (
          <div className="w-full h-full grid place-items-center ob-ink3 text-[11px]">
            No image
          </div>
        )}

        <div className="ob-favslot" onClick={(e) => e.stopPropagation()}>
          <WishlistToggle product={product} />
        </div>
      </div>

      <div className="ob-card-meta">
        {(product.colour || product.department) && (
          <div className="ob-card-brand">
            {product.colour || product.department}
          </div>
        )}
        <div
          className="ob-card-title cursor-pointer"
          onClick={() => onOpen(product)}
        >
          {product.enhanced_name || "Product"}
        </div>
        <div className="ob-card-price">
          ${Number(shown || 0).toFixed(2)}
          {hasDiscount && (
            <span className="ob-card-mrp">
              ${Number(product.price).toFixed(2)}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

/** Section heading with an optional right-hand slot. */
function SectionHead({ eyebrow, title, sub, right }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-4">
      <div className="min-w-0">
        {eyebrow && <span className="ob-eyebrow">{eyebrow}</span>}
        <h2 className="ob-display text-[clamp(1.4rem,3vw,1.8rem)] leading-tight mt-1">
          {title}
        </h2>
        {sub && <p className="text-[12.5px] ob-ink2 mt-1">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

function ProductGridSkeleton({ count = 8 }) {
  return (
    <div className="ob-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>
          <div
            className="ob-skel"
            style={{ aspectRatio: "3 / 4", borderRadius: "var(--ob-r)" }}
          />
          <div className="ob-skel h-2.5 w-14 mt-3" />
          <div className="ob-skel h-3 w-full mt-2" />
          <div className="ob-skel h-3 w-16 mt-2" />
        </div>
      ))}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();

  // Use state for customerId to react to auth changes
  const [customerId, setCustomerId] = useState(() => {
    const userProfile = getUserProfile();
    return userProfile?.customer_id || null;
  });

  // Listen for auth changes (e.g., after login)
  useEffect(() => {
    const updateCustomerId = () => {
      const userProfile = getUserProfile();
      const newCustomerId = userProfile?.customer_id || null;
      setCustomerId((prev) => {
        if (prev !== newCustomerId) {
          return newCustomerId;
        }
        return prev;
      });
    };

    // Check immediately
    updateCustomerId();

    // Listen for auth state change events
    const handleAuthChange = (event) => {
      // Update customerId when auth changes
      setTimeout(updateCustomerId, 100); // Small delay to ensure cookies are set
    };

    window.addEventListener("authStateChanged", handleAuthChange);

    // Also poll periodically as a fallback (in case event doesn't fire)
    const intervalId = setInterval(updateCustomerId, 1000);

    return () => {
      window.removeEventListener("authStateChanged", handleAuthChange);
      clearInterval(intervalId);
    };
  }, []);

  const handleProductClick = useCallback(
    (product) => {
      if (!product?.id) return;

      // Check if user is logged in
      const userProfile = getUserProfile();
      const customerId = userProfile?.customer_id;

      if (!customerId) {
        // Store product ID and open login modal
        sessionStorage.setItem("pendingProductId", product.id);
        window.dispatchEvent(
          new CustomEvent("openLoginModal", {
            detail: { productId: product.id },
          })
        );
        return;
      }

      // User is logged in, navigate to product
      navigate(`/item/${product.id}`);
    },
    [navigate]
  );

  // Products — the featured grid, for guests and signed-in shoppers alike.
  // (The signed-in "promotions" / "recommended for you" rails were retired
  // on 2026-10-22 along with the Cloud Functions behind them.)
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 8;

  // Hero carousel state
  const [currentSlide, setCurrentSlide] = useState(0);

  // Fetch featured
  const loadFeatured = useCallback(
    async (page) => {
      setLoadingFeatured(true);
      try {
        const data = await fetchFeaturedProducts(page, perPage);
        setFeaturedProducts(
          data.records.map((r) => ({
            id: r.article_id,
            enhanced_name: r.enhanced_prod_name || r.prod_name || "Product",
            enhanced_description:
              r.enhanced_description || r.detail_desc || "No description",
            price: parseFloat(r.price) || 0,
            image: getImageUrl(r.image_url || r.image_uri),
            colour: r.colour_group_name || "Unknown",
          }))
        );
        setTotalPages(data.pages);
      } catch (err) {
        console.error("Error loading featured products:", err);
      } finally {
        setLoadingFeatured(false);
      }
    },
    [perPage]
  );

  useEffect(() => {
    loadFeatured(currentPage);
  }, [currentPage, loadFeatured]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    }
  }, [currentPage, totalPages]);

  const handlePrev = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  }, [currentPage]);

  // Hero carousel navigation - memoized to prevent recreation
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroImages.length) % heroImages.length
    );
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  // Auto-rotate. Re-armed on every slide change (manual or automatic) so the
  // progress line under the copy always runs the full interval.
  useEffect(() => {
    const timeout = setTimeout(nextSlide, HERO_AUTOPLAY_MS);
    return () => clearTimeout(timeout);
  }, [currentSlide, nextSlide]);


  // Navigate for a category tile / its button. Shared so the card and the
  // button inside it can never drift apart.
  const openCategory = (category) => {
    window.dispatchEvent(new CustomEvent(category.event));
  };

  // Loading
  if (loadingFeatured) {
    return (
      <div className="max-w-[1480px] mx-auto px-4 sm:px-6 py-8">
        <div
          className="ob-skel"
          style={{ height: 420, borderRadius: "var(--ob-r-xl)" }}
        />
        <div className="ob-skel h-3 w-24 mt-10" />
        <div className="ob-skel h-8 w-60 mt-3 mb-6" />
        <ProductGridSkeleton />
      </div>
    );
  }

  const slide = heroImages[currentSlide];

  return (
    <div className="ob-viewin">
      {/* ══════════ Hero ══════════ */}
      <section className="max-w-[1480px] mx-auto px-4 sm:px-6 pt-5">
        <div className="ob-lphero">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentSlide}
              src={slide.src}
              alt={slide.alt}
              className="ob-lphero-img"
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
            />
          </AnimatePresence>

          {/* A scrim, not a tint: the copy has to stay legible over any
              photograph that lands here. */}
          <div className="ob-lphero-scrim" />

          <div className="ob-lphero-body">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <span className="ob-lphero-label">{slide.top_label}</span>

                <h1 className="ob-display ob-lphero-title">{slide.Title}</h1>

                <p className="ob-lphero-sub">{slide.Buttom_label}</p>

                {/* One solid call to action, the rest as an underlined link —
                    two equal buttons compete; this reads as a choice. */}
                {slide.ButtonsName?.length > 0 && (
                  <div className="ob-lphero-actions">
                    {slide.ButtonsName.map((btn, btnIndex) =>
                      btnIndex === 0 ? (
                        <button
                          key={btnIndex}
                          type="button"
                          className="ob-btn ob-btn-lg ob-btn-onhero-solid ob-lphero-cta"
                        >
                          {btn.name}
                        </button>
                      ) : (
                        <button key={btnIndex} type="button" className="ob-lphero-link">
                          {btn.name}
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M5 12h14m-5-5 5 5-5 5" />
                          </svg>
                        </button>
                      )
                    )}
                  </div>
                )}

                {/* The point of the storefront, said once, where the eye
                    already is. */}
                <p className="ob-lphero-hint">
                  Or ask Orbiee and simply <em>say what you want</em>.
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Editorial slide control: a counter and one progress line per
              slide (the active one fills over the autoplay interval), with
              the arrows tucked into the opposite corner. */}
          {heroImages.length > 1 && (
            <div className="ob-lphero-foot">
              <div className="ob-lphero-progress">
                <span className="ob-lphero-count" aria-hidden="true">
                  {String(currentSlide + 1).padStart(2, "0")}
                  <span className="ob-lphero-count-total">
                    {" "}/ {String(heroImages.length).padStart(2, "0")}
                  </span>
                </span>
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goToSlide(index)}
                    className={`ob-lphero-tick${
                      index === currentSlide ? " ob-on" : ""
                    }${index < currentSlide ? " ob-done" : ""}`}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-current={index === currentSlide ? "true" : undefined}
                  >
                    <span style={{ animationDuration: `${HERO_AUTOPLAY_MS}ms` }} />
                  </button>
                ))}
              </div>

              <div className="ob-lphero-arrows">
                <button
                  type="button"
                  onClick={prevSlide}
                  className="ob-lphero-arrow"
                  aria-label="Previous slide"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M19 12H5m5-5-5 5 5 5" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={nextSlide}
                  className="ob-lphero-arrow"
                  aria-label="Next slide"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14m-5-5 5 5-5 5" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ══════════ Reassurance ══════════ */}
      <section className="max-w-[1480px] mx-auto px-4 sm:px-6 mt-10">
        <ul className="ob-assure ob-stagger">
          {[
            {
              title: "Complimentary shipping",
              body: "On every order, with no minimum spend.",
              glyph: (
                <>
                  <path d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5Z" />
                  <path d="M3.5 7.5 12 12l8.5-4.5M12 12v9" />
                </>
              ),
            },
            {
              title: "Secure checkout",
              body: "Encrypted end to end, every time.",
              glyph: (
                <>
                  <rect x="5" y="10.5" width="14" height="10" rx="2" />
                  <path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" />
                </>
              ),
            },
            {
              title: "Your stylist, always on",
              body: "Orbiee is here whenever you are.",
              glyph: (
                <>
                  <circle cx="12" cy="12" r="8.5" />
                  <path d="M12 7.5V12l3 2" />
                </>
              ),
            },
          ].map(({ title, body, glyph }) => (
            <li key={title} className="ob-assure-item">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ob-assure-glyph"
                aria-hidden="true"
              >
                {glyph}
              </svg>
              <div className="ob-assure-title">{title}</div>
              <p className="ob-assure-body">{body}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* ══════════ Shop by category ══════════ */}
      <section className="max-w-[1480px] mx-auto px-4 sm:px-6 mt-12">
        <SectionHead
          eyebrow="Browse"
          title="Shop by category"
          sub="Or tell Orbiee the occasion and let it choose."
        />

        <div className="ob-catgrid ob-stagger">
          {Sectiontwoimages.map((category) => (
            <button
              key={category.key}
              type="button"
              onClick={() => openCategory(category)}
              className={`ob-cattile ob-cattile-${category.size}`}
              aria-label={`${category.link} — ${category.title}`}
            >
              <img
                src={category.src}
                alt=""
                width={category.width}
                height={category.height}
                loading="lazy"
                style={{ objectPosition: category.focus }}
              />
              <span className="ob-cattile-scrim" />

              <span className="ob-cattile-body">
                <span className="ob-cattile-eyebrow">{category.eyebrow}</span>
                <span className="ob-display ob-cattile-title">
                  {category.title}
                </span>
                <span className="ob-cattile-link">
                  {category.link}
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14m-5-5 5 5-5 5" />
                  </svg>
                </span>
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ══════════ Products ══════════ */}
      <div className="max-w-[1480px] mx-auto px-4 sm:px-6 mt-14 pb-16 space-y-14">
        <section>
          <SectionHead
            eyebrow="The edit"
            title="Featured products"
            sub="A cross-section of the collection."
            right={
              totalPages > 1 ? (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={handlePrev}
                    disabled={currentPage === 1}
                    className="ob-iconbtn ob-hair"
                    aria-label="Previous page"
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </button>
                  <span className="text-[11.5px] ob-ink3 ob-tabnum whitespace-nowrap">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    className="ob-iconbtn ob-hair"
                    aria-label="Next page"
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>
              ) : null
            }
          />

          {featuredProducts.length > 0 ? (
            <div className="ob-grid ob-stagger">
              {featuredProducts.map((product) => (
                <ProductCard
                    key={product.id}
                    product={product}
                    onOpen={handleProductClick}
                  />
              ))}
            </div>
          ) : (
            <ProductGridSkeleton count={4} />
          )}

          {/* Signed-out nudge — guests only, at the point they hit the
              wall. Signed-in shoppers see the same grid without it. */}
          {!customerId && (
            <div className="ob-signinnudge mt-8">
              <span
                className="ob-blob"
                style={{
                  width: 190,
                  height: 190,
                  top: -110,
                  right: 30,
                  background: "rgba(255,255,255,.7)",
                }}
              />
              <div className="relative">
                <div className="ob-display text-[23px] leading-tight" style={{ color: "var(--ob-ink)" }}>
                  Sign in to shop by voice
                </div>
                <p
                  className="text-[12.5px] mt-1.5 max-w-[440px] leading-relaxed"
                  style={{ color: "var(--ob-ink-2)" }}
                >
                  Orbiee remembers your size, your fit and what is in your bag —
                  then finds things for you in your own words.
                </p>
              </div>
              <button
                type="button"
                className="ob-btn ob-btn-lg ob-btn-onhero-solid relative shrink-0"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("openLoginModal"))
                }
              >
                Sign in
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
